import { motion } from "framer-motion";
import { Droplets, EyeOff, TrendingDown } from "lucide-react";
import { Eyebrow, Reveal, fadeUp, stagger } from "./Reveal";

const CARDS = [
    {
        tag: "Exhibit A-1",
        title: "O Vazamento Invisível",
        text: "Você investe em aquisição, os leads chegam, mas a conversão cai. O dinheiro entra e sai sem deixar lucro.",
        Icon: Droplets,
        viz: (
            <svg viewBox="0 0 240 64" className="mt-9 h-20 w-full" fill="none">
                <motion.path
                    d="M6 14 L50 20 L92 18 L134 30 L178 28 L234 52"
                    stroke="#c9a227"
                    strokeWidth="1.5"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                />
                <line x1="92" y1="18" x2="102" y2="42" stroke="#c9a227" strokeDasharray="2 3" opacity="0.5" />
                <circle cx="103" cy="47" r="2" fill="#c9a227" />
                <line x1="178" y1="28" x2="189" y2="54" stroke="#c9a227" strokeDasharray="2 3" opacity="0.5" />
                <circle cx="190" cy="59" r="2" fill="#c9a227" />
            </svg>
        ),
    },
    {
        tag: "Exhibit A-2",
        title: "A Cegueira de Dados",
        text: "Decisões tomadas no 'achismo'. Sem métricas reais de CAC, LTV e Retenção, cada real investido em marketing é um risco.",
        Icon: EyeOff,
        viz: (
            <svg viewBox="0 0 240 64" className="mt-9 h-20 w-full" fill="none">
                <circle cx="64" cy="32" r="10" stroke="#262626" strokeWidth="1" />
                <circle cx="64" cy="32" r="20" stroke="#262626" strokeWidth="1" />
                <circle cx="64" cy="32" r="30" stroke="#262626" strokeWidth="1" />
                <path d="M64 32 L64 2 A30 30 0 0 1 90 17 Z" fill="rgba(201,162,39,0.14)" />
                <circle cx="64" cy="32" r="2.5" fill="#c9a227" />
                <line x1="130" y1="32" x2="170" y2="32" stroke="#c9a227" strokeWidth="1.5" opacity="0.7" />
                <line x1="184" y1="32" x2="224" y2="32" stroke="#c9a227" strokeWidth="1.5" opacity="0.7" />
            </svg>
        ),
    },
    {
        tag: "Exhibit A-3",
        title: "A Falsa Solução",
        text: "Contratar mais gente ou gastar mais com anúncios antes de corrigir o processo interno só acelera o prejuízo.",
        Icon: TrendingDown,
        viz: (
            <svg viewBox="0 0 240 64" className="mt-9 h-20 w-full" fill="none">
                <rect x="20" y="44" width="24" height="16" fill="#262626" />
                <rect x="60" y="36" width="24" height="24" fill="#262626" />
                <rect x="100" y="26" width="24" height="34" fill="#262626" />
                <rect x="140" y="14" width="24" height="46" fill="#262626" />
                <rect x="196" y="8" width="24" height="52" stroke="#c9a227" strokeWidth="1.5" strokeDasharray="4 3" />
                <path d="M208 -1 L216 12 L200 12 Z" transform="translate(0,2)" stroke="#c9a227" strokeWidth="1.2" />
            </svg>
        ),
    },
];

export default function Problem() {
    return (
        <section id="problema" className="relative px-6 py-28 lg:px-10 lg:py-36" data-testid="problem-section">
            <div className="mx-auto max-w-7xl">
                <div className="max-w-2xl">
                    <Reveal>
                        <Eyebrow>Exhibit A — O Problema</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="mt-7 font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-5xl">
                            Por que empresas sólidas{" "}
                            <em className="italic text-gold">estagnam</em>?
                        </h2>
                    </Reveal>
                </div>

                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.15 }}
                    className="mt-16 grid gap-5 md:grid-cols-3"
                >
                    {CARDS.map((c, i) => (
                        <motion.article
                            key={c.tag}
                            variants={fadeUp}
                            whileHover={{ y: -6 }}
                            className="group relative border border-line bg-[#1a1a1a] p-8 transition-colors duration-300 hover:border-gold-line"
                            data-testid={`problem-card-${i + 1}`}
                        >
                            <div className="flex items-start justify-between">
                                <c.Icon size={26} strokeWidth={1.4} className="text-gold" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
                                    {c.tag}
                                </span>
                            </div>
                            <h3 className="mt-9 font-serif text-2xl text-white">{c.title}</h3>
                            <p className="mt-4 text-[15px] leading-relaxed text-[#9a9a9a]">{c.text}</p>
                            <div className="opacity-80 transition-opacity duration-300 group-hover:opacity-100">
                                {c.viz}
                            </div>
                        </motion.article>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
